// Runs immediately rather than on DOMContentLoaded: nothing here touches the DOM, and on
// these ~3 MB PDPs the wait cost the code to anyone who clicked through first. The IIFE
// keeps `attributes`/`body` out of the global lexical environment.
(() => {
  // Only set during synchronous execution, so it cannot be read from a callback.
  const scriptTag = document.currentScript || document.getElementById('wkz-tracking-script');

  // The GemPages layouts render the tag without id or data-cart-update-url, where reading
  // .dataset off null threw and killed tracking for the whole page.
  const cartUpdateUrl =
    scriptTag?.dataset?.cartUpdateUrl || `${window.Shopify?.routes?.root || '/'}cart/update.js`;

  // Attribution window from the theme setting (Apps & Integrations → WKZ tracking).
  const wkzDays = Number(scriptTag?.dataset?.wkzDays) || 7;

  const expired = '; expires=Thu, 01 Jan 1970 00:00:00 GMT';

  // The widest domain the browser accepts, so the wkz cookie also reaches the other subdomains
  // (account.achtrihof.com from www.achtrihof.com). Probed instead of taking the last two labels:
  // a public suffix like myshopify.com is rejected, and the cookie would not be set at all.
  const cookieDomain = (() => {
    const labels = window.location.hostname.split('.');
    for (let i = labels.length - 2; i >= 0; i--) {
      const domain = labels.slice(i).join('.');
      document.cookie = `wkz_probe=1; domain=${domain}; path=/`;
      if (getCookie('wkz_probe')) {
        document.cookie = `wkz_probe=${expired}; domain=${domain}; path=/`;
        return domain;
      }
    }
    return ''; // localhost: host-only
  })();

  function setCookie(name, value, days) {
    let expires = '';
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = `; expires=${date.toUTCString()}`;
    }
    // Only wkz is shared. wkz_cart is compared with Shopify's host-only cart cookie, and host
    // records the visitor's own host, so both stay host-only.
    const domain = name === 'wkz' && cookieDomain ? `; domain=${cookieDomain}` : '';
    // Drop the host-only copy an earlier version wrote, which would shadow this one on reads.
    if (domain) document.cookie = `${name}=${expired}; path=/`;
    // Encoded: the value is read back into cart attributes, and a raw ; would truncate it.
    document.cookie = `${name}=${encodeURIComponent(value || '')}${expires}; path=/${domain}`;
  }

  function getCookie(name) {
    const cookieArr = document.cookie.split(';');
    for (let i = 0; i < cookieArr.length; i++) {
      const cookie = cookieArr[i].trim();
      if (cookie.indexOf(`${name}=`) === 0) {
        const value = cookie.substring(name.length + 1, cookie.length);
        try {
          return decodeURIComponent(value);
        } catch {
          return value; // written raw by an earlier version of this script
        }
      }
    }
    return null;
  }

  function fetchConfig(type = 'json') {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
    };
  }

  const urlParams = new URLSearchParams(window.location.search);
  const currentHost = window.location.host;

  if (!getCookie('host')) {
    setCookie('host', currentHost);
  }

  const attributes = { host: currentHost };

  // An order replaces the cart, so a cart other than the one wkz was stamped on at checkout
  // means that order is placed: a later one counts only after a new ?wkz landing.
  const stampedCart = getCookie('wkz_cart');
  if (stampedCart && stampedCart !== getCookie('cart')) {
    setCookie('wkz', '', -1);
    setCookie('wkz_cart', '', -1);
  }

  // Ad platforms sometimes encode extra params into the value with & (%26) or spaces (+).
  const wkz = urlParams.has('wkz') ? urlParams.get('wkz').split(/[& ]/)[0] : '';

  // The cookie is read back only at checkout (below), not on every pageview, so it lasts
  // until the next order or wkzDays, whichever comes first. An empty ?wkz= is skipped
  // because Shopify treats an empty attribute value as a removal.
  if (wkz) {
    attributes.wkz = wkz;
    setCookie('wkz', wkz, wkzDays);
  }

  const body = JSON.stringify({ attributes });

  // Sentry is defined only on bodylab-de (vb-store-specific-scripts), and only in the
  // layouts that render that snippet — theme.liquid and the GemPages ones, never bare.liquid.
  // This asset ships to every store, so the guard is required, same shape as main.js.
  function report(message, error) {
    const text = `WKZ Tracking Error: ${message}.`;
    if (typeof Sentry === 'undefined') {
      console.error(text, error ?? '');
      return;
    }
    Sentry.captureException(error ?? new Error(text), {
      tags: { file: 'wkz-tracking.js' },
      extra: {
        message: text,
        cartUpdateUrl,
        body,
        cartCookie: getCookie('cart'),
        cookieEnabled: navigator.cookieEnabled,
        webdriver: navigator.webdriver,
      },
    });
  }

  const cartUrl = `${window.Shopify?.routes?.root || '/'}cart.js`;
  const maxAttempts = 3;
  const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

  // On a first visit there is no cart yet, and an app embed in content_for_header posts to
  // cart/update.js at the same moment. Each cookieless write creates its own cart and the last
  // Set-Cookie wins, so this write can land on a cart the visitor never sees again. Read the
  // cart back once the writes have settled and write again until the attributes are on it.
  async function capture(attempt) {
    // keepalive: a fast click-through off the landing page must not cancel the request.
    const response = await fetch(cartUpdateUrl, { ...fetchConfig(), body, keepalive: true });
    // A Cloudflare challenge or a 429 resolves normally, so the status has to be read:
    // otherwise a dropped capture is indistinguishable from the script never running.
    // 409 is Shopify's answer to too many simultaneous cart writes — the race itself — so retry it,
    // like a transient 5xx. A 403 is a bot challenge, not worth reporting (BODYLAB-EUR-DPJ).
    if (!response.ok) {
      if (response.status === 403) return;
      if ((response.status !== 409 && response.status < 500) || attempt >= maxAttempts) {
        report(`cart update returned ${response.status}`);
        return;
      }
      await wait(1000 * attempt);
      await capture(attempt + 1);
      return;
    }

    await wait(1000 * attempt);
    // A failed read means "not confirmed yet", not a failed capture: fall through and write again.
    const cart = await fetch(cartUrl)
      .then(res => res.json())
      .catch(() => null);
    if (Object.keys(attributes).every(key => cart?.attributes?.[key] === attributes[key])) return;

    if (attempt >= maxAttempts) {
      // Without a persisted cart cookie (cookies blocked, automated browser) every write lands
      // on a new cart, so the attributes can never stick: not a tracking bug (BODYLAB-EUR-DPG).
      if (!getCookie('cart') || navigator.webdriver) return;
      report(`attributes missing from the cart after ${attempt} writes`);
      return;
    }
    await capture(attempt + 1);
  }

  capture(1).catch(error => {
    report('the cart update request failed', error);
  });

  // Stamp again from the cookie at checkout, when the cart is certainly the visitor's own,
  // and remember that cart so the cookie is dropped once its order is placed (see top).
  function checkoutAttributes() {
    const stamped = { host: currentHost };
    const code = getCookie('wkz');
    if (code) {
      stamped.wkz = code;
      // An empty wkz_cart would switch order detection off, so only record a real cart.
      const cart = getCookie('cart');
      if (cart) setCookie('wkz_cart', cart, wkzDays);
    }
    return stamped;
  }

  // The drawer and cart page check out through a native POST /cart with name="checkout";
  // attributes[...] fields ride on that same request, so nothing can race them.
  document.addEventListener('submit', event => {
    if (event.submitter?.name !== 'checkout') return;
    // Replace, not update: a form resubmitted after the cookie was dropped must not keep the old code.
    event.target.querySelectorAll('input[data-wkz-stamp]').forEach(input => input.remove());
    Object.entries(checkoutAttributes()).forEach(([key, value]) => {
      const input = Object.assign(document.createElement('input'), {
        type: 'hidden',
        name: `attributes[${key}]`,
        value,
      });
      input.dataset.wkzStamp = '';
      event.target.append(input);
    });
  });

  // For flows that jump straight to /checkout (skip-cart buy buttons). Never throws, and gives
  // up after 3s so a hanging request (e.g. a Cloudflare challenge) cannot block checkout.
  window.wkzTracking = {
    stampCart: () =>
      fetch(cartUpdateUrl, {
        ...fetchConfig(),
        body: JSON.stringify({ attributes: checkoutAttributes() }),
        signal: AbortSignal.timeout(3000),
      })
        .then(response => {
          if (!response.ok) report(`checkout stamp returned ${response.status}`);
        })
        .catch(error => report('the checkout stamp failed', error)),
  };
})();
